import { ApiClientError } from '@/lib/api';

export interface MutationErrorDescriptor {
  key: string;
  values?: Record<string, unknown>;
}

export interface MutationErrorResolution {
  descriptor: MutationErrorDescriptor;
  code?: string;
  status?: number;
  message?: string;
}

export interface ResolveMutationErrorOptions {
  defaultKey?: string;
}

const DEFAULT_FALLBACK_KEY = 'generic.unexpectedError';

/**
 * Checks if a string looks like a translation key (dot notation, no spaces).
 * Translation keys follow patterns like: "auth.unauthorized", "dashboard.conflict"
 *
 * This heuristic distinguishes translation keys from plain English error messages.
 */
function isTranslationKey(value: string): boolean {
  // Must be non-empty, contain at least one dot, and have no spaces
  return value.length > 0 && value.includes('.') && !value.includes(' ');
}

/**
 * Centralized map of error codes to translation keys.
 * All keys are relative to the 'errors' namespace in errors.json.
 *
 * IMPORTANT: This map serves as a FALLBACK for errors where the server
 * does not provide an explicit translation key. Server-provided keys
 * (in error.message or error.details.translationKey) take precedence.
 *
 * Use cases for this map:
 * - Network failures (TypeError from fetch)
 * - Third-party API errors (GitHub SDK)
 * - Legacy endpoints that only return codes
 * - Unexpected errors with no translation key
 */
const CODE_TRANSLATION_MAP: Record<string, MutationErrorDescriptor> = {
  // Rate limiting
  RATE_LIMITED: { key: 'rateLimit.exceeded' },
  RATE_LIMIT: { key: 'rateLimit.exceeded' },

  // Validation
  VALIDATION_ERROR: { key: 'validation.invalidInput' },
  INVALID_INPUT: { key: 'validation.invalidInput' },
  INVALID_DATA: { key: 'validation.invalidData' },

  // Authentication & Authorization
  UNAUTHORIZED: { key: 'auth.unauthorized' },
  FORBIDDEN: { key: 'auth.forbidden' },
  SESSION_EXPIRED: { key: 'auth.sessionExpired' },

  // Resource errors
  NOT_FOUND: { key: 'notFound.resource' },
  RESOURCE_NOT_FOUND: { key: 'notFound.resource' },

  // Conflicts
  CONFLICT: { key: 'conflict.alreadyExists' },
  IDEMPOTENCY_CONFLICT: { key: 'conflict.alreadyExists' },

  // OAuth errors
  REQUEST_ACCESS_REQUIRED: { key: 'oauth.requestAccessRequired' },
  // Canonical AUTH_MISSING and the legacy PROVIDER_AUTH_MISSING alias share
  // the same descriptor; the legacy row is retained as a legacy-tolerant fallback.
  AUTH_MISSING: { key: 'oauth.providerAuthMissing' },
  PROVIDER_AUTH_MISSING: { key: 'oauth.providerAuthMissing' },
  SCOPE_INSUFFICIENT: { key: 'oauth.scopeInsufficient' },
  RESOURCE_NOT_SHARED: { key: 'oauth.resourceNotShared' },
  CAPABILITY_MISSING: { key: 'oauth.capabilityMissing' },
  OAUTH_TIMEOUT: { key: 'oauth.timeout' },
  POPUP_BLOCKED: { key: 'oauth.popupBlocked' },

  // Provider-specific: GitHub
  GITHUB_API_ERROR: { key: 'providers.github.apiError' },
  GITHUB_ACCOUNT_NOT_LINKED: { key: 'providers.github.accountNotLinked' },

  // Network errors
  NETWORK_ERROR: { key: 'network.serverError' },
  TIMEOUT: { key: 'network.timeout' },

  // Server errors
  INTERNAL_ERROR: { key: 'generic.unexpectedError' },
};

function normalizeCode(code?: string): string | undefined {
  if (!code) return undefined;
  return code.trim().toUpperCase();
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}

function resolveRetryAfter(details: unknown): number | undefined {
  if (!isRecord(details)) {
    return undefined;
  }

  const raw = details.retryAfter;
  if (typeof raw === 'number' && Number.isFinite(raw)) {
    return raw;
  }
  if (typeof raw === 'string') {
    const parsed = Number(raw);
    if (Number.isFinite(parsed)) {
      return parsed;
    }
  }
  return undefined;
}

/**
 * Resolves an error to a translation descriptor for user-facing messages.
 *
 * Resolution priority (server is source of truth):
 * 1. Server-provided translation key in error.message (preferred)
 * 2. Explicit translation key in error.details.translationKey
 * 3. Fallback to CODE_TRANSLATION_MAP for legacy/external errors
 * 4. Default fallback key
 *
 * This priority ensures server-specified messages take precedence,
 * enabling precise error tracing and context-specific translations.
 */
export function resolveMutationError(
  error: unknown,
  options: ResolveMutationErrorOptions = {}
): MutationErrorResolution {
  const defaultKey = options.defaultKey ?? DEFAULT_FALLBACK_KEY;

  if (error instanceof ApiClientError) {
    const code = normalizeCode(error.code);
    const retryAfter = resolveRetryAfter(error.details);

    // Build base values for interpolation
    const baseValues =
      typeof retryAfter === 'number' ? { retryAfter } : undefined;

    // Priority 1: Server-provided translation key in error.message
    // Server sets this via: jsonErr(code, 'translation.key', details)
    if (isTranslationKey(error.message)) {
      return {
        descriptor: {
          key: error.message,
          ...(baseValues && { values: baseValues }),
        },
        ...(code && { code }),
        status: error.status,
        message: error.message,
      };
    }

    // Priority 2: Explicit translation key in details.translationKey
    // Alternative path for cases where message contains something else
    if (typeof error.details === 'object' && error.details !== null) {
      const details = error.details as Record<string, unknown>;
      const translationKey = details.translationKey;
      if (
        typeof translationKey === 'string' &&
        isTranslationKey(translationKey)
      ) {
        const translationValues = isRecord(details.translationValues)
          ? {
              ...baseValues,
              ...(details.translationValues as Record<string, unknown>),
            }
          : baseValues;

        return {
          descriptor: {
            key: translationKey,
            ...(translationValues && { values: translationValues }),
          },
          ...(code && { code }),
          status: error.status,
          message: error.message,
        };
      }
    }

    // Priority 3: Fallback to CODE_TRANSLATION_MAP
    // Used for legacy endpoints, third-party errors, network failures
    const descriptorFromCode = code ? CODE_TRANSLATION_MAP[code] : undefined;
    if (descriptorFromCode) {
      const values = baseValues
        ? { ...(descriptorFromCode.values ?? {}), ...baseValues }
        : descriptorFromCode.values;

      return {
        descriptor: {
          key: descriptorFromCode.key,
          ...(values && { values }),
        },
        ...(code && { code }),
        status: error.status,
        message: error.message,
      };
    }

    // Priority 4: Default fallback
    return {
      descriptor: { key: defaultKey },
      ...(code && { code }),
      status: error.status,
      message: error.message,
    };
  }

  if (error instanceof Error) {
    return {
      descriptor: { key: defaultKey },
      message: error.message,
    };
  }

  return {
    descriptor: { key: defaultKey },
  };
}
